import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Landmark, PlugZap, Unplug, Save, CheckCircle2, AlertCircle, Copy } from "lucide-react";

const PROVIDER_FIELDS = {
  xero: [["account_code", "Sales account code (e.g. 200)"], ["tax_type", "Tax type (e.g. OUTPUT)"], ["invoice_status", "Status (AUTHORISED / DRAFT)"]],
  qbo: [["environment", "Environment (sandbox / production)"], ["item_id", "Item ID for concrete sales"]],
  myob: [["cf_username", "Company-file username"], ["cf_password", "Company-file password"], ["income_account_uid", "Income account UID"], ["tax_code_uid", "Tax code UID (GST)"]],
};

export default function AccountingHubCard() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("xero");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/accounting/settings").then((r) => { setData(r.data); setForm({}); });
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const p = params.get("accounting");
    if (!p) return;
    if (params.get("connected")) toast.success(`${p.toUpperCase()} connected — invoices can now be pushed`);
    else if (params.get("error")) toast.error(`${p.toUpperCase()} connection failed: ${params.get("error")}`);
    setTab(p);
    params.delete("accounting"); params.delete("connected"); params.delete("error");
    setParams(params, { replace: true });
  }, [params, setParams]);

  if (!data) return null;
  const conf = data.providers[tab] || {};
  const val = (k) => (form[k] !== undefined ? form[k] : conf[k] || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/accounting/settings/${tab}`, form);
      await load();
      toast.success("Credentials saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const connect = async () => {
    try {
      if (Object.keys(form).length) await api.put(`/accounting/settings/${tab}`, form);
      const { data: r } = await api.get(`/accounting/${tab}/connect`);
      window.location.assign(r.url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const disconnect = async () => { await api.post(`/accounting/${tab}/disconnect`); await load(); toast.success("Disconnected"); };
  const setActive = async (p) => { await api.put("/accounting/settings", { provider: p }); await load(); toast.success(`${data.providers[p].label} is now the Push target`); };

  return (
    <div className="border border-industrial bg-surface" data-testid="accounting-hub-card">
      <div className="px-5 py-4 border-b border-industrial flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Landmark className="w-4 h-4 text-primary-orange" />
          <div>
            <div className="label-tag">ACCOUNTING INTEGRATION HUB</div>
            <h2 className="heading font-black uppercase text-base md:text-lg">Xero · QuickBooks Online · MYOB</h2>
          </div>
        </div>
        <span className="mono text-[10px] uppercase tracking-widest" data-testid="accounting-active-provider">
          Push target: <span className="text-primary-orange">{data.active_provider ? data.providers[data.active_provider].label : "none"}</span>
        </span>
      </div>
      <div className="flex border-b border-industrial">
        {Object.keys(data.providers).map((p) => (
          <button key={p} type="button" data-testid={`accounting-tab-${p}`} onClick={() => { setTab(p); setForm({}); }}
            className={`px-4 py-2 mono text-[11px] uppercase tracking-wider flex items-center gap-1.5 border-b-2 ${tab === p ? "border-primary-orange text-primary-orange" : "border-transparent text-muted-industrial"}`}>
            {data.providers[p].connected ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <AlertCircle className="w-3 h-3 opacity-50" />}
            {data.providers[p].label}
          </button>
        ))}
      </div>
      <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="space-y-3">
          <div className={`p-3 border ${conf.connected ? "border-emerald-600/60 bg-emerald-500/5" : "border-industrial"}`} data-testid={`accounting-status-${tab}`}>
            <div className="label-tag">Connection</div>
            <div className="font-bold text-sm">{conf.connected ? "CONNECTED" : "NOT CONNECTED"}</div>
            <div className="mono text-[10px] text-muted-industrial break-all">
              {conf.tenant_name || conf.company_file_name || (conf.realm_id ? `Realm ${conf.realm_id}` : "Enter credentials, then Connect")}
            </div>
            {conf.last_error && <div className="mono text-[10px] text-red-400">{conf.last_error}</div>}
          </div>
          <div>
            <div className="label-tag">Redirect URI (register in {conf.label} developer app)</div>
            <button type="button" data-testid={`accounting-redirect-${tab}`} className="mono text-[10px] text-left break-all text-sky-400 hover:underline flex items-start gap-1"
              onClick={() => { navigator.clipboard?.writeText(data.redirect_uris[tab]); toast.success("Copied"); }}>
              <Copy className="w-3 h-3 mt-0.5 shrink-0" />{data.redirect_uris[tab]}
            </button>
          </div>
          <div className="flex flex-col gap-2">
            {conf.connected
              ? <Button data-testid={`accounting-disconnect-${tab}`} variant="outline" className="rounded-none" onClick={disconnect}><Unplug className="w-4 h-4 mr-2" />Disconnect</Button>
              : <Button data-testid={`accounting-connect-${tab}`} className="rounded-none bg-primary-orange text-white" onClick={connect}><PlugZap className="w-4 h-4 mr-2" />Connect {conf.label}</Button>}
            {conf.connected && data.active_provider !== tab && <Button data-testid={`accounting-set-active-${tab}`} variant="ghost" className="rounded-none text-xs" onClick={() => setActive(tab)}>Make Push target</Button>}
          </div>
        </div>
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="label-tag">Client ID</Label><Input data-testid={`accounting-client-id-${tab}`} value={val("client_id")} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="rounded-none bg-transparent border-industrial mono text-xs" /></div>
          <div><Label className="label-tag">Client Secret</Label><Input type="password" data-testid={`accounting-client-secret-${tab}`} value={val("client_secret")} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} className="rounded-none bg-transparent border-industrial mono text-xs" placeholder={conf.client_secret ? "•••••••• (saved)" : ""} /></div>
          {PROVIDER_FIELDS[tab].map(([k, lbl]) => (
            <div key={k}><Label className="label-tag">{lbl}</Label><Input type={k === "cf_password" ? "password" : "text"} data-testid={`accounting-${k}-${tab}`} value={val(k)} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-none bg-transparent border-industrial mono text-xs" /></div>
          ))}
          <div className="md:col-span-2 flex justify-end">
            <Button data-testid={`accounting-save-${tab}`} onClick={save} disabled={saving} variant="outline" className="rounded-none"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save credentials"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
